import { LitElement, ReactiveController } from 'lit';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';
import { ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';
import { TreeSearchEvent } from '../types';
import { NetworkV2Analytics } from '../../networkAppContainer/helpers/types';

export class NetworkTreeSearchController implements ReactiveController {
  public isLoading = false;
  public searchResult: Array<ProtectedEntity> = [];

  private host: LitElement;
  private readLimit = 25;
  private currentPage = 1;
  private searchTotalResults: number;
  private searchString: string;
  private userRootNodeId: string;

  constructor(host: LitElement) {
    this.host = host;
    host.addController(this);
  }

  hostConnected() {}

  public async search(searchString: string) {
    if (searchString === '') {
      this.searchString = searchString;
      return;
    }

    // nothing should happen if equal
    if (this.searchString === searchString) {
      return;
    }

    this.searchString = searchString;
    this.currentPage = 1;

    this.host.dispatchEvent(new CustomEvent(TreeSearchEvent.START_FETCHING, { bubbles: true, composed: true }));
    this.isLoading = true;
    this.host.requestUpdate();
    await this.setUserNodeId();
    await this.readEntities(searchString);
    this.isLoading = false;
    this.host.requestUpdate();
  }

  public async loadMoreResults() {
    if (this.searchTotalResults <= this.searchResult.length) {
      return;
    }

    const scrollDiv = this.host.shadowRoot?.querySelector('.tree-search-result');
    if (!scrollDiv) {
      return;
    }

    if (
      Math.ceil(scrollDiv.scrollTop + scrollDiv.clientHeight) === scrollDiv.scrollHeight &&
      scrollDiv.scrollTop !== 0
    ) {
      this.currentPage += 1;

      this.isLoading = true;
      this.host.requestUpdate();
      await this.readEntities(this.searchString);
      this.isLoading = false;
      this.host.requestUpdate();
    }
  }

  private async readEntities(searchString: string) {
    const acceptedTypes = [
      Constants.Network.Filters.Types.COMPANIES_PARTNER_MANAGED,
      Constants.Network.Filters.Types.COMPANY_FOLDERS,
      Constants.Network.Filters.Types.FOLDERS_NETWORK_V2,
    ];

    const response = await ConsoleRequest.createRequest<{ data: Array<ProtectedEntity>; total: number }>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'ProtectedEntitiesEPS',
      method: 'readRecords',
      payload: {
        additionalFilters: {
          depth: Constants.ProtectedEntities.EPS.FILTERS.DEPTH.SHOW_ALL_ITEMS_RECURSIVELY,
          type: acceptedTypes,
        },
        filter: [
          {
            property: 'name',
            value: searchString,
          },
        ],
        nodeId: this.userRootNodeId,
        limit: this.readLimit,
        page: this.currentPage,
      },
    });

    analytics.trackPageInteraction({
      reference: `${NetworkV2Analytics.TRACKING_NAME}:${NetworkV2Analytics.IDENTIFIER_NETWORK_TREE}`,
      asset: NetworkV2Analytics.ASSET_TREE_SEARCH_USED,
    });

    if (this.currentPage !== 1) {
      this.searchResult.push(...response.data);
    } else {
      this.searchResult = response.data;
    }
    this.searchTotalResults = response.total;
  }

  private async setUserNodeId() {
    if (this.userRootNodeId !== undefined) {
      return;
    }

    const response = await ConsoleRequest.createRequest<{ _id: string }>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'ProtectedEntitiesEPS',
      method: 'getRoot',
      payload: {},
    });
    this.userRootNodeId = response._id;
  }
}
